import { type ApiError, api } from "@/lib/api";
import type {
  CompetitionDraft,
  CompetitionMember,
  CompetitionSettings,
  CompetitionSummary,
  CompetitionsList,
  PendingInvite,
  PublicInvitePreview,
} from "@/types";

export async function createCompetition(draft: CompetitionDraft) {
  return api.post<{ data: string }>("/competitions", {
    name: draft.name,
    description: draft.description,
    type: draft.type,
    startDate: draft.startDate,
    endDate: draft.endDate,
    targetSteps: draft.targetSteps,
    dailyGoalSteps: draft.dailyGoalSteps,
    teamScoring: draft.teamScoring,
    timezone: draft.timezone,
    visibility: draft.visibility,
    badgeIds: draft.badgeIds,
    scopeMode: draft.scopeMode,
    scopeGroupIds: draft.scopeGroupIds,
  });
}

export async function joinCompetition(inviteCode: string) {
  return api.post("/competitions/join", { inviteCode });
}

export async function makeMemberAdmin(competitionId: string, userId: string) {
  return api.post(`/competitions/${competitionId}/promote`, { targetUserId: userId });
}

export async function demoteFromAdmin(competitionId: string, userId: string) {
  return api.post(`/competitions/${competitionId}/demote`, { targetUserId: userId });
}

export async function removeCompetitionMember(competitionId: string, userId: string) {
  return api.post(`/competitions/${competitionId}/remove-member`, { targetUserId: userId });
}

export async function addCompetitionMember(competitionId: string, userId: string) {
  return api.post(`/competitions/${competitionId}/add-member`, { targetUserId: userId });
}

export async function updateCompetitionSettings(
  competitionId: string,
  settings: CompetitionSettings,
) {
  return api.patch(`/competitions/${competitionId}/settings`, settings);
}

export async function getCompetitionMembers(
  competitionId: string,
): Promise<{ data: CompetitionMember[] | null; error: ApiError | null }> {
  return api.get<CompetitionMember[]>(`/competitions/${competitionId}/members`);
}

export async function getMyCompetitions(options?: {
  limit?: number;
  offset?: number;
  past?: boolean;
}): Promise<{
  data: CompetitionsList | null;
  error: ApiError | null;
}> {
  const params = new URLSearchParams();
  if (options?.limit) params.set("limit", String(options.limit));
  if (options?.offset) params.set("offset", String(options.offset));
  if (options?.past) params.set("past", "true");
  const qs = params.toString();
  const path = qs ? `/competitions?${qs}` : "/competitions";
  return api.get<CompetitionsList>(path);
}

export async function getCompetitionById(
  competitionId: string,
): Promise<{ data: CompetitionSummary | null; error: ApiError | null }> {
  return api.get<CompetitionSummary>(`/competitions/${competitionId}`);
}

export async function getCompetitionByInviteCode(
  inviteCode: string,
): Promise<{ data: CompetitionSummary | null; error: ApiError | null }> {
  return api.get<CompetitionSummary>(`/competitions/invite/${inviteCode.toUpperCase()}`);
}

export async function deleteCompetition(competitionId: string) {
  return api.delete(`/competitions/${competitionId}`);
}

export async function createRematch(
  competitionId: string,
): Promise<{ data: string | null; error: ApiError | null }> {
  return api.post<string>(`/competitions/${competitionId}/rematch`, {});
}

export interface DailyMessageResponse {
  message: string | null;
  date: string;
}

export async function getDailyMessage(
  competitionId: string,
): Promise<{ data: DailyMessageResponse | null; error: ApiError | null }> {
  return api.get<DailyMessageResponse>(`/competitions/${competitionId}/daily-message`);
}

export interface CompletionSummaryResponse {
  message: string;
}

export async function getCompletionSummary(
  competitionId: string,
): Promise<{ data: CompletionSummaryResponse | null; error: ApiError | null }> {
  return api.get<CompletionSummaryResponse>(`/competitions/${competitionId}/completion-summary`);
}

export async function getPublicInvitePreview(
  code: string,
): Promise<{ data: PublicInvitePreview | null; error: ApiError | null }> {
  return api.get<PublicInvitePreview>(`/competitions/invite-public/${code.toUpperCase()}`);
}

export async function getPendingInvites(): Promise<{
  data: PendingInvite[] | null;
  error: ApiError | null;
}> {
  return api.get<PendingInvite[]>("/invites/pending");
}
